import type { Structure } from '../analysis/structure';
import type { DocModel } from '../model/document';
import type { Align, LineRule } from '../model/props';
import type { Profile } from '../profiles/types';

export type Severity = 'error' | 'warning' | 'info';

export type Category = 'page' | 'text' | 'structure' | 'objects' | 'references' | 'hygiene';

export const CATEGORIES: ReadonlyArray<{ id: Category; title: string }> = [
  { id: 'page', title: 'Страница' },
  { id: 'text', title: 'Текст' },
  { id: 'structure', title: 'Структура' },
  { id: 'objects', title: 'Рисунки, таблицы, формулы' },
  { id: 'references', title: 'Источники' },
  { id: 'hygiene', title: 'Чистота текста' },
];

export interface ParaFix {
  jc?: Align;
  /** twip; > 0 — отступ первой строки, < 0 — выступ. */
  indFirst?: number;
  indLeft?: number;
  spacingBefore?: number;
  spacingAfter?: number;
  line?: number;
  lineRule?: LineRule;
  pageBreakBefore?: boolean;
  /** Не отрывать от следующего абзаца. */
  keepNext?: boolean;
  /** Уровень структуры (0 — первый): по нему Word собирает содержание. */
  outlineLvl?: number;
  /** Снять автоматическую нумерацию (numId = 0). */
  removeNumbering?: boolean;
}

export interface RunFix {
  font?: string;
  /** Полупункты. */
  sz?: number;
  color?: 'auto';
  bold?: boolean;
  caps?: boolean;
  underline?: false;
  highlight?: false;
}

export type FixAction =
  | { kind: 'para'; paragraph: number; set: ParaFix }
  | { kind: 'runs'; paragraph: number; set: RunFix }
  /** Замена текста [start, end) в видимом тексте абзаца; start === end — вставка. */
  /** silent — невидимая правка (неразрывный пробел): даже в режиме рецензирования без пометок. */
  | { kind: 'text'; paragraph: number; start: number; end: number; expect: string; replace: string; silent?: boolean }
  | {
      kind: 'section';
      section: number;
      widthTw?: number;
      heightTw?: number;
      margins?: Partial<Record<'top' | 'bottom' | 'left' | 'right' | 'gutter', number>>;
      clearPageRestart?: boolean;
    }
  | { kind: 'titlePage'; section: number }
  /** Выравнивание абзаца в колонтитуле (part — путь части, index — номер абзаца в ней). */
  | { kind: 'hfAlign'; part: string; index: number; jc: Align }
  | { kind: 'updateFields' }
  | { kind: 'meta'; clear: Array<'creator' | 'lastModifiedBy'> }
  /** Номера страниц: поле PAGE в колонтитуле. font и sz (полупункты) — как у основного текста. */
  | { kind: 'pageNumbers'; position: 'bottom' | 'top'; align: 'center' | 'right'; font?: string; sz?: number }
  /** Маркер уровня списка в numbering.xml: «•» → «–». */
  | { kind: 'bullet'; numId: number; ilvl: number; text: string; font: string }
  /** Автоматическое содержание вместо старого (replace), после заголовка «Содержание» (after) или с заголовком перед абзацем (before). */
  | {
      kind: 'toc';
      replace?: { first: number; last: number };
      after?: number;
      before?: number;
      /** Заголовок «СОДЕРЖАНИЕ» — при вставке перед абзацем. */
      title?: { text: string; bold: boolean };
      entries: Array<{ paragraph: number; level: number; text: string; page: number }>;
      depth: number;
      /** Правый край текста, twip. */
      tabPos: number;
      font?: string;
      sz?: number;
      /** Межстрочный интервал пунктов, 240 — одинарный. */
      line?: number;
    };

export interface Finding {
  /** Стабильный идентификатор: при повторной проверке того же файла совпадает. */
  id: string;
  rule: string;
  severity: Severity;
  message: string;
  /** Абзац, к которому относится замечание; нет — замечание ко всему документу. */
  paragraph?: number;
  /** Все абзацы, если замечание собрано из одинаковых замечаний к разным абзацам. */
  paragraphs?: number[];
  /** Фрагменты текста абзаца для подсветки. */
  marks?: Array<[number, number]>;
  fix?: FixAction[];
  fixLabel?: string;
}

/** Текст, зависящий от профиля: одна строка для всех или своя для каждого профиля. */
export type ByProfile = string | Readonly<Record<string, string>>;

/** Пустая строка для профиля означает, что в этом стандарте такого пункта нет. */
export function forProfile(v: ByProfile | undefined, profileId: string): string | undefined {
  if (v === undefined || typeof v === 'string') return v || undefined;
  return (v[profileId] ?? v['gost-7.32-2017'] ?? Object.values(v)[0]) || undefined;
}

export interface RuleMeta {
  id: string;
  category: Category;
  title: string;
  severity: Severity;
  /** Пункт стандарта — общий или для каждого профиля свой. */
  gost?: ByProfile;
  /** Короткое объяснение «почему это важно» для интерфейса. */
  why?: ByProfile;
  /** Одинаковые замечания ко многим абзацам объединяются в одно; формы слова для счётчика: «абзац», «абзаца», «абзацев». */
  aggregate?: readonly [string, string, string];
  /** Важность в лабораторных, практических и контрольных работах, где требование мягче. */
  shortWork?: Severity;
}

export type ReportInput = Omit<Finding, 'id' | 'rule' | 'severity'> & { severity?: Severity };

export interface RuleContext {
  model: DocModel;
  s: Structure;
  profile: Profile;
  report(f: ReportInput): void;
}

export interface Rule extends RuleMeta {
  check(ctx: RuleContext): void;
}

export const defineRule = (r: Rule): Rule => r;
